import React, {Fragment, useMemo} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber} from 'react-intl'
import {Box, Flex, Skeleton, Text, useSlotRecipe, VisuallyHidden} from '@chakra-ui/react3'
import {useCurrency} from '../../../hooks/use-currency'
import {useCurrentCustomer} from '../../../hooks/use-current-customer'
import displayPriceRecipe from './index.recipe'

/**
 * The DisplayPrice is a visual representation of all price settings of products
 * @param {'xpdp-configurator'} variant - the variant of the product tile:
 * - `xpdp-configurator`: price display in xpdp configurator (first line: "price incl. vat", second line: <price> <original price strikethrough>)
 *
 */
const DisplayPrice = ({
    price,
    displayPrice,
    currency,
    loading,
    isFree,
    hasDiscount,
    variant,
    alignment
}) => {
    const {currency: activeCurrency} = useCurrency()
    const {isCustomNetPriceEnabled} = useCurrentCustomer().data

    const recipe = useSlotRecipe({recipe: displayPriceRecipe})
    const styles = useMemo(
        () =>
            recipe({
                variant,
                alignment,
                isFree,
                hasDiscount,
                isCustomNetPriceEnabled
            }),
        [recipe, variant, alignment, isFree, hasDiscount, isCustomNetPriceEnabled]
    )

    const SkeletonComponent = loading ? Skeleton : Fragment
    const skeletonProps = loading ? {loading, as: 'div'} : {}

    return (
        <Box css={styles.container}>
            <SkeletonComponent {...skeletonProps}>
                <Text css={styles.taxesLabel} data-testid="taxes">
                    {!isCustomNetPriceEnabled ? (
                        <>
                            <FormattedMessage
                                id="global.taxes_included"
                                defaultMessage="tax included"
                            />
                        </>
                    ) : (
                        <FormattedMessage
                            id="global.taxes_not_included"
                            defaultMessage="tax not included"
                        />
                    )}
                </Text>
            </SkeletonComponent>
            <Flex css={styles.priceOrder}>
                <SkeletonComponent {...skeletonProps}>
                    <Box css={styles.priceContainer}>
                        {hasDiscount && (
                            <VisuallyHidden>
                                <FormattedMessage
                                    defaultMessage="Discounted price: "
                                    id="item_attributes.label.discounted_price"
                                />
                            </VisuallyHidden>
                        )}
                        {isFree ? (
                            <Text css={styles.freeLabel} data-testid="isFree">
                                <FormattedMessage defaultMessage="Free" id="global.free" />
                            </Text>
                        ) : (
                            <Text css={styles.price} data-testid="displayPrice">
                                <FormattedNumber
                                    style="currency"
                                    currency={currency || activeCurrency}
                                    value={displayPrice}
                                />
                            </Text>
                        )}
                    </Box>
                </SkeletonComponent>
                {hasDiscount && (
                    <SkeletonComponent {...skeletonProps}>
                        <Text css={styles.originalPrice} data-testid="price">
                            <VisuallyHidden>
                                <FormattedMessage
                                    defaultMessage="Original price: "
                                    id="item_attributes.label.original_price"
                                />
                            </VisuallyHidden>
                            <FormattedNumber
                                style="currency"
                                currency={currency || activeCurrency}
                                value={price}
                            />
                        </Text>
                    </SkeletonComponent>
                )}
            </Flex>
        </Box>
    )
}

DisplayPrice.propTypes = {
    price: PropTypes.number,
    displayPrice: PropTypes.number,
    currency: PropTypes.string,
    loading: PropTypes.bool,
    isFree: PropTypes.bool,
    hasDiscount: PropTypes.bool,
    variant: PropTypes.string,
    alignment: PropTypes.oneOf(['start', 'center', 'end'])
}

export default DisplayPrice
